/**
 * AuthContextV2 — autenticação via cookie httpOnly (SEC-2).
 *
 * Strategy:
 *  1. Login: POST /webhook/auth/verify-password → cookie `delivai_session` (se SESSION_AUTH_ENABLED).
 *  2. Front nunca lê o token; fetch com `credentials: 'include'`.
 *  3. Boot: GET /webhook/auth/me.
 *  4. Refresh silencioso a cada 10min via POST /webhook/auth/refresh.
 *  5. Logout: POST /webhook/auth/logout.
 *
 * Não substitui AuthContext.tsx legado IMEDIATAMENTE — aninhado sob o legado.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ApiClientError } from "../services/api/errors";
import { authService, type SessionMe } from "../services/auth";
import { queryClient } from "../services/api/queryClient";

type AuthV2State =
  | { status: "loading"; session: null }
  | { status: "authenticated"; session: SessionMe }
  | { status: "anonymous"; session: null };

type AuthV2Api = {
  state: AuthV2State;
  login: (cnpj: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refetchSession: () => Promise<void>;
};

const Ctx = createContext<AuthV2Api | null>(null);
const REFRESH_INTERVAL_MS = 10 * 60_000;

export const AuthProviderV2: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AuthV2State>({
    status: "loading",
    session: null,
  });
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const refetchSession = useCallback(async () => {
    try {
      const me = await authService.me();
      if (me?.authenticated && me.ok !== false) {
        setState({ status: "authenticated", session: me });
      } else {
        setState({ status: "anonymous", session: null });
      }
    } catch (err) {
      if (err instanceof ApiClientError && err.isUnauthorized()) {
        setState({ status: "anonymous", session: null });
      } else {
        // Transição: se sessão ainda off no back, não bloquear o legado.
        setState({ status: "anonymous", session: null });
      }
    }
  }, []);

  const login = useCallback(
    async (cnpj: string, password: string) => {
      await authService.verifyPassword(cnpj, password);
      await refetchSession();
    },
    [refetchSession]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setState({ status: "anonymous", session: null });
      queryClient.clear();
    }
  }, []);

  useEffect(() => {
    void refetchSession();
  }, [refetchSession]);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    refreshTimer.current = setInterval(() => {
      authService.refreshCookie().catch((err) => {
        if (err instanceof ApiClientError && err.isUnauthorized()) {
          setState({ status: "anonymous", session: null });
        }
      });
    }, REFRESH_INTERVAL_MS);
    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [state.status]);

  const api = useMemo<AuthV2Api>(
    () => ({ state, login, logout, refetchSession }),
    [state, login, logout, refetchSession]
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
};

export function useAuthV2(): AuthV2Api {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useAuthV2 deve ser usado dentro de <AuthProviderV2>");
  }
  return ctx;
}
